import React, { useState, useEffect } from "react";
import { Search, Filter, Users, Calendar, Gamepad2, Crown, Trophy, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageBanner } from "@/components/common/PageBanner";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { fetchCommunities } from "@/services/communityService";
import { fetchGames } from "@/services/gameService";
import { fetchBannersByPage } from "@/services/bannersService";
import { CommunityFilters } from "@/types/community";
import { Game } from "@/types/game";
import { Banner } from "@/types/banner";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import CommunityCard, { CommunityCardData } from "@/components/community/CommunityCard";
import { useFeaturedItemsForPage } from "@/hooks/useFeaturedItems";

// Tipo para comunidades com dados do mentor
interface CommunityWithMentor {
  id: string;
  name: string;
  slug: string;
  description: string;
  cover_image_url?: string;
  mentor_id: string;
  is_active: boolean;
  member_count: number;
  created_at: string;
  updated_at: string;
  games?: {
    id: string;
    name: string;
    icon?: string;
  };
  mentor?: {
    profile: {
      nickname: string;
      image_url?: string;
    };
  };
}

const ComunidadesPage = () => {
  const [communities, setCommunities] = useState<CommunityWithMentor[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGame, setSelectedGame] = useState<string>("");
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Query para buscar comunidades em destaque
  const { data: featuredComunidades } = useFeaturedItemsForPage('comunidade');

  const filters: CommunityFilters = {
    search: searchTerm || undefined,
    game_id: selectedGame || undefined,
    limit: 12,
    offset: 0
  };

  useEffect(() => {
    loadGames();
    loadBanners();
  }, []);

  useEffect(() => {
    loadCommunities();
  }, [searchTerm, selectedGame]);

  const loadGames = async () => {
    try {
      const gamesData = await fetchGames();
      setGames(gamesData);
    } catch (error) {
      console.error('Erro ao carregar jogos:', error);
    }
  };

  const loadBanners = async () => {
    try {
      const bannersData = await fetchBannersByPage('comunidades');
      setBanners(bannersData);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
    }
  };

  const loadCommunities = async () => {
    try {
      setLoading(true);
      const response = await fetchCommunities(filters);
      setCommunities(response.communities as CommunityWithMentor[]);
      setHasMore(response.has_more);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Erro ao carregar comunidades:', error);
      toast({
        title: "Erro",
        description: "Falha ao carregar comunidades. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleGameFilter = (gameId: string) => {
    setSelectedGame(gameId === "all" ? "" : gameId);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedGame("");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleVisitCommunity = (communityId: string) => {
    const community = communities.find(c => c.id === communityId);
    if (community?.slug) {
      navigate(`/comunidades/${community.slug}`);
    } else {
      navigate(`/comunidade/${communityId}`);
    }
  };

  // Separar comunidades em destaque das normais
  const featuredComunidadeIds = featuredComunidades?.map(item => item.item_id) || [];
  const featuredComunidadesList = communities.filter(comunidade => featuredComunidadeIds.includes(comunidade.id));
  const regularComunidadesList = communities.filter(comunidade => !featuredComunidadeIds.includes(comunidade.id));

  // Ordenar comunidades em destaque por posição
  const sortedFeaturedComunidades = featuredComunidadesList.sort((a, b) => {
    const positionA = featuredComunidades?.find(item => item.item_id === a.id)?.position || 999;
    const positionB = featuredComunidades?.find(item => item.item_id === b.id)?.position || 999;
    return positionA - positionB;
  });

  // Função para obter ícone de posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2: return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3: return <Star className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  // Função para obter cor da borda de destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1: return 'border-yellow-500 shadow-yellow-500/20';
      case 2: return 'border-gray-400 shadow-gray-400/20';
      case 3: return 'border-amber-600 shadow-amber-600/20';
      default: return '';
    }
  };

  // Converter dados para o formato esperado pelo CommunityCard
  const formatCommunityData = (community: CommunityWithMentor, isFeatured: boolean = false): CommunityCardData => {
    return {
      id: community.id,
      name: community.name,
      slug: community.slug,
      description: community.description,
      cover_url: community.cover_image_url,
      cover_image_url: community.cover_image_url,
      member_count: community.member_count || 0,
      is_featured: isFeatured,
      created_at: community.created_at,
      game: {
        name: community.games?.name || 'Jogo não especificado',
        icon: community.games?.icon
      },
      mentor: {
        profile: {
          nickname: community.mentor?.profile?.nickname || 'Mentor',
          image_url: community.mentor?.profile?.image_url
        }
      }
    };
  };

  return (
    <div className="min-h-screen bg-[#0A0F16]">
      <Navbar />
      {/* Banner da página - sempre exibe um banner com gradiente */}
      <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
        {banners && banners.length > 0 ? (
          <PageBanner banners={banners} />
        ) : (
          <div className="relative w-full overflow-hidden">
            {/* Gradiente consistente aplicado sempre */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#18222F]/50 to-[#0A0F16] z-10 pointer-events-none"></div>
            
            {/* Banner padrão quando não há banners customizados */}
            <div className="w-full h-80 bg-gradient-to-r from-[#1A2233] via-[#223342] to-[#2A3749] flex items-center justify-center">
              <div className="text-center text-white relative z-20">
                <h2 className="text-4xl font-bold mb-4">Comunidades Pro Player</h2>
                <p className="text-xl text-gray-300">Conecte-se com outros gamers e forme sua comunidade</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filtros e Busca */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Busca */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar comunidades..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-[#1A2333] border-[#222A35] text-white placeholder-gray-400"
              />
            </div>

            {/* Filtro por Jogo */}
            <div className="md:w-64">
              <Select value={selectedGame || "all"} onValueChange={handleGameFilter}>
                <SelectTrigger className="bg-[#1A2333] border-[#222A35] text-white">
                  <Gamepad2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Todos os jogos" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A2333] border-[#222A35]">
                  <SelectItem value="all" className="text-white hover:bg-[#242C37]">
                    Todos os jogos
                  </SelectItem>
                  {games.map((game) => (
                    <SelectItem 
                      key={game.id} 
                      value={game.id}
                      className="text-white hover:bg-[#242C37]"
                    >
                      {game.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Limpar Filtros */}
            {(searchTerm || selectedGame) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-[#222A35] text-gray-400 hover:text-white hover:bg-[#242C37]"
              >
                <Filter className="h-4 w-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>

          {/* Contador de Resultados */}
          <div className="text-gray-400 text-sm">
            {loading ? "Carregando..." : `${totalCount} comunidade${totalCount !== 1 ? 's' : ''} encontrada${totalCount !== 1 ? 's' : ''}`}
          </div>
        </div>

        {/* Lista de Comunidades */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : communities.length === 0 ? (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="text-gray-400 mb-4">
                <Users size={48} className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Nenhuma comunidade encontrada
                </h3>
                <p>
                  {searchTerm || selectedGame
                    ? "Tente ajustar os filtros para encontrar comunidades."
                    : "Ainda não há comunidades criadas. Seja o primeiro!"}
                </p>
              </div>
              {(searchTerm || selectedGame) && (
                <Button onClick={clearFilters} className="mt-4">
                  Limpar Filtros
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Comunidades em Destaque */}
            {sortedFeaturedComunidades.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Star className="h-6 w-6 text-[#00B6F0]" />
                  Comunidades em Destaque
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedFeaturedComunidades.map(community => {
                    const featuredItem = featuredComunidades?.find(item => item.item_id === community.id);
                    const position = featuredItem?.position || 0;
                    return (
                      <div key={community.id} className="relative">
                        {/* Badge de posição */}
                        <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                          {getPositionIcon(position)}
                        </div>
                        <div className={`border-2 ${getFeaturedBorderClass(position)} rounded-xl shadow-lg`}>
                          <CommunityCard
                            community={formatCommunityData(community, true)}
                            showOwnerBadge={false}
                            showMentorInfo={true}
                            onVisit={handleVisitCommunity}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Outras Comunidades */}
            <div className="mb-8">
              {sortedFeaturedComunidades.length > 0 && (
                <h2 className="text-2xl font-bold text-white mb-4">
                  Outras Comunidades
                </h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regularComunidadesList.map((community) => (
                  <CommunityCard
                    key={community.id}
                    community={formatCommunityData(community)}
                    showOwnerBadge={false}
                    showMentorInfo={true}
                    onVisit={handleVisitCommunity}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Botão Carregar Mais */}
        {hasMore && !loading && (
          <div className="text-center mt-8">
            <Button
              onClick={() => {
                // TODO: Implementar carregamento de mais comunidades
                console.log("Carregar mais comunidades");
              }}
              variant="outline"
              className="border-[#222A35] text-gray-400 hover:text-white hover:bg-[#242C37]"
            >
              Carregar Mais
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComunidadesPage; 